import { BoardSchema, type Board } from './board.js';
import type { Tile } from './tile.js';
import { MAX_TILES_PER_BOARD, type TileSize } from './constants.js';
import { getTileTypeDef } from './registry.js';
import { TileSchema } from './tile.js';

/**
 * ============================================================================
 * IMMUTABLE BOARD-EDIT OPERATIONS  (tech-lead seam amendment, 2026-07-24)
 * ============================================================================
 * The ONLY sanctioned way the app mutates a board. Edit mode (drag/resize/add/
 * remove — core-flows §3) calls these; the future NL builder emits them too.
 * Every op is:
 *
 *   PURE        — no ambient state, deterministic.
 *   IMMUTABLE   — never mutates its input; returns a brand-new Board (or an error).
 *   TOTAL       — never throws; every failure is a typed `BoardOpError` result.
 *   SELF-VALIDATING — every successful result is re-parsed through `BoardSchema`,
 *                 so an op can NEVER produce an invalid board. If a change would
 *                 violate the schema (bad position, broken referential integrity,
 *                 hostile size), the op returns `invalid-board` instead of an
 *                 invalid Board. This is the same firewall clones go through
 *                 (ADR-002 §4/§7) applied to every in-app edit.
 *
 * POSITION MODEL (read this before building placement UI):
 *   A board keeps IDENTITY (`tiles`, each with a stable `id` + its `display.size`)
 *   SEPARATE from POSITION (`layout`, one `{tileId,x,y,w,h}` entry per tile) — see
 *   ADR-002 §6.1. A `Position` is the grid ORIGIN `{x,y}` (top-left cell, 0-based);
 *   the tile's FOOTPRINT `{w,h}` is derived from its `display.size` via `sizeToWH`
 *   and kept in lock-step by these ops (resize changes BOTH, move changes NEITHER).
 *   Platform-dev should think: "moving a tile writes a new layout entry; it never
 *   rewrites the tile config — and resizing writes both, so size can't drift."
 *
 *   NOTE: these ops validate each tile's own footprint (size legal for its type,
 *   position within grid bounds) but do NOT compute anti-overlap packing — that
 *   reflow/collision logic is platform-dev's layout engine (ADR-001), which decides
 *   the final `{x,y}` and then commits it through `moveTile`. Ops guarantee the
 *   board stays SCHEMA-valid; the layout engine guarantees it stays visually packed.
 *
 *   BOUNDS VALIDATION (H2): BoardSchema.superRefine enforces that every layout entry
 *   satisfies `x + w <= grid.cols` and `y + h <= MAX_GRID_ROWS` (constants.ts).
 *   Any op that would push a tile off-grid is caught here and returns `invalid-board`.
 * ============================================================================
 */

/** A grid placement: the 0-based origin (top-left) cell of a tile. */
export interface Position {
  readonly x: number;
  readonly y: number;
}

/** A tile footprint in grid cells, derived from `display.size`. */
export interface Footprint {
  readonly w: number;
  readonly h: number;
}

/** Typed failure reasons — a closed set so callers can switch exhaustively. */
export type BoardOpError =
  /** Add target id already exists on the board (ADR-002 §3: ids unique per board). */
  | 'id-collision'
  /** Adding would exceed MAX_TILES_PER_BOARD (ADR-002 §7 #4/#8). */
  | 'max-tiles'
  /** No tile with the given id on the board (remove/move/resize). */
  | 'unknown-tile'
  /** Requested size is not in the tile type's registry `allowedSizes`. */
  | 'invalid-size'
  /** The resulting board failed BoardSchema (bad position, broken invariant, …). */
  | 'invalid-board';

/** Discriminated result: a new validated Board, or a typed error. Never an exception. */
export type BoardOpResult =
  | { readonly ok: true; readonly board: Board }
  | { readonly ok: false; readonly error: BoardOpError; readonly detail?: string };

const ok = (board: Board): BoardOpResult => ({ ok: true, board });
const err = (error: BoardOpError, detail?: string): BoardOpResult =>
  detail === undefined ? { ok: false, error } : { ok: false, error, detail };

/**
 * Configure a tile's `source.params` in-place.
 *
 * This is the SOLE sanctioned seam for param editing (D-011: sports team picker
 * and any future per-type param editor commit through this function, never by
 * mutating tile config directly).
 *
 * Behaviour:
 *   1. Finds the tile by `tileId` — fails with `unknown-tile` if not present.
 *   2. Retrieves the tile type's `paramsSchema` from the registry (ADR-002 §3.1).
 *      For types with a null params schema (Pro-only types like calendar.peek)
 *      or no-fetch tiles, the new params must still satisfy the registered schema
 *      when present; returns `invalid-board` if the type has no schema and
 *      params were supplied (fail-closed).
 *   3. Merges `newParams` into the existing params (shallow — params schemas use
 *      strict Zod objects so any unknown key is rejected by step 4).
 *   4. Validates the merged params with the tile type's `paramsSchema` — rejects
 *      unknown keys (SSRF / prototype-pollution firewall), wrong types, and
 *      out-of-range values exactly as TileSchema does at parse time.
 *   5. Re-validates the whole resulting tile through `TileSchema` to ensure glance
 *      targets remain valid for the new config (e.g. if a type change were ever
 *      to follow a configure op, glance targets would be re-checked).
 *   6. Re-validates the whole resulting board through `BoardSchema` — makes the op
 *      self-validating (same contract as all other ops here).
 *   7. Returns a NEW board; NEVER mutates the input.
 *
 * `newParams` is a partial update: only the supplied keys are changed; the rest
 * of the existing params are preserved.  This lets the team-picker update just
 * `teamId` without needing to know the current `league`, for example.
 *
 * Errors returned:
 *   - `unknown-tile`  — no tile with `tileId` on the board.
 *   - `invalid-board` — the merged params fail the type's schema, or the
 *                       resulting board fails BoardSchema.
 */
export function configureTile(
  board: Board,
  tileId: string,
  newParams: Record<string, unknown>,
): BoardOpResult {
  const tile = board.tiles.find((t) => t.id === tileId);
  if (!tile) {
    return err('unknown-tile', `no tile "${tileId}" on the board`);
  }

  const def = getTileTypeDef(tile.type);

  // Fail-closed: if the registered params schema is null (Pro-only phase-deferred
  // type), we cannot safely validate — reject rather than pass unvalidated params.
  if (def.paramsSchema === null) {
    return err(
      'invalid-board',
      `${tile.type} has no params schema registered; cannot configure params safely`,
    );
  }

  // Merge new params over the existing params (preserves untouched keys).
  const existingParams: Record<string, unknown> =
    tile.source?.params !== undefined
      ? (tile.source.params as Record<string, unknown>)
      : {};
  const mergedParams = { ...existingParams, ...newParams };

  // Validate the merged params against the registered per-type schema.
  // `.strict()` on every paramsSchema means unknown keys are rejected here —
  // the SSRF / prototype-pollution firewall (ADR-002 §7 #1/#5).
  const paramsResult = def.paramsSchema.safeParse(mergedParams);
  if (!paramsResult.success) {
    return err(
      'invalid-board',
      `params validation failed for ${tile.type}: ${paramsResult.error.message}`,
    );
  }

  // Build the new tile with the validated params.
  const updatedSource = tile.source
    ? { ...tile.source, params: paramsResult.data as Record<string, unknown> }
    : { provider: def.allowedProviders[0] ?? 'local', params: paramsResult.data as Record<string, unknown> };

  const candidateTile = { ...tile, source: updatedSource };

  // Re-validate the whole tile through TileSchema (glance targets, size, etc.)
  const tileResult = TileSchema.safeParse(candidateTile);
  if (!tileResult.success) {
    return err(
      'invalid-board',
      `tile re-validation failed after configure: ${tileResult.error.message}`,
    );
  }

  const candidateBoard = {
    ...board,
    tiles: board.tiles.map((t) => (t.id === tileId ? tileResult.data : t)),
  };
  return validated(candidateBoard);
}

/**
 * Map a `display.size` string (e.g. "2x1") to its grid footprint `{w,h}`. The
 * size enum is `WxH` by construction (constants.ts TILE_SIZES), so this parse is
 * total over TileSize. Pure.
 */
export function sizeToWH(size: TileSize): Footprint {
  const [w, h] = size.split('x').map((n) => Number.parseInt(n, 10));
  return { w: w ?? 1, h: h ?? 1 };
}

/** Does the board already contain a tile with this id? */
function hasTile(board: Board, tileId: string): boolean {
  return board.tiles.some((t) => t.id === tileId);
}

/**
 * Re-validate a candidate board object through BoardSchema and wrap the outcome.
 * This is the single choke point that makes "an op can never produce an invalid
 * board" true: EVERY op ends here. A parse failure becomes `invalid-board`, never
 * a thrown error or a leaked malformed Board.
 */
function validated(candidate: unknown): BoardOpResult {
  const parsed = BoardSchema.safeParse(candidate);
  return parsed.success ? ok(parsed.data) : err('invalid-board', parsed.error.message);
}

/**
 * Add `tile` to `board` at `position`. The layout entry's footprint is derived
 * from the tile's own `display.size` (single source of truth for size). Fails on
 * id collision (#3 below), on exceeding the tile cap (#2), or if the result is
 * not schema-valid (#5 — e.g. a hostile tile or bad position).
 *
 * NOTE: `tile` is assumed already schema-valid (it came from `parseTile`/the
 * picker). BoardSchema re-validates it anyway as part of the whole-board parse, so
 * a malformed tile still cannot slip in — it surfaces as `invalid-board`.
 */
export function addTile(board: Board, tile: Tile, position: Position): BoardOpResult {
  if (hasTile(board, tile.id)) {
    return err('id-collision', `tile id "${tile.id}" already on the board`);
  }
  // Fail with a SPECIFIC error before we build a candidate that BoardSchema would
  // reject with a generic `invalid-board` — callers want to distinguish "full".
  if (board.tiles.length >= MAX_TILES_PER_BOARD) {
    return err('max-tiles', `board already at the ${MAX_TILES_PER_BOARD}-tile cap`);
  }
  const { w, h } = sizeToWH(tile.display.size);
  const candidate = {
    ...board,
    tiles: [...board.tiles, tile],
    layout: [...board.layout, { tileId: tile.id, x: position.x, y: position.y, w, h }],
  };
  return validated(candidate);
}

/**
 * Remove the tile with `tileId` and its layout entry (keeps referential integrity
 * — a layout entry can never outlive its tile). Fails if the id is not present.
 */
export function removeTile(board: Board, tileId: string): BoardOpResult {
  if (!hasTile(board, tileId)) {
    return err('unknown-tile', `no tile "${tileId}" on the board`);
  }
  const candidate = {
    ...board,
    tiles: board.tiles.filter((t) => t.id !== tileId),
    layout: board.layout.filter((l) => l.tileId !== tileId),
  };
  return validated(candidate);
}

/**
 * Move a tile to a new grid origin. Writes a NEW layout entry (new `{x,y}`, same
 * `{w,h}`); the tile CONFIG is never touched (position vs identity — ADR-002 §6.1).
 * Fails on unknown id, or if the new position is out of grid bounds (`invalid-board`).
 */
export function moveTile(board: Board, tileId: string, position: Position): BoardOpResult {
  if (!hasTile(board, tileId)) {
    return err('unknown-tile', `no tile "${tileId}" on the board`);
  }
  const candidate = {
    ...board,
    layout: board.layout.map((l) =>
      l.tileId === tileId ? { ...l, x: position.x, y: position.y } : l,
    ),
  };
  return validated(candidate);
}

/**
 * Resize a tile to `size`. Updates BOTH the tile's `display.size` AND its layout
 * `{w,h}` so the two representations of size can never drift apart. Enforces the
 * registry: `size` must be in the tile type's `allowedSizes` (a config can't
 * request a size the type doesn't support — mirrors the TileSchema rule). Fails on
 * unknown id, illegal size, or a non-schema-valid result.
 */
export function resizeTile(board: Board, tileId: string, size: TileSize): BoardOpResult {
  const tile = board.tiles.find((t) => t.id === tileId);
  if (!tile) {
    return err('unknown-tile', `no tile "${tileId}" on the board`);
  }
  const def = getTileTypeDef(tile.type);
  if (!def.allowedSizes.includes(size)) {
    return err('invalid-size', `size "${size}" not allowed for ${tile.type}`);
  }
  const { w, h } = sizeToWH(size);
  const candidate = {
    ...board,
    tiles: board.tiles.map((t) =>
      t.id === tileId ? { ...t, display: { ...t.display, size } } : t,
    ),
    layout: board.layout.map((l) => (l.tileId === tileId ? { ...l, w, h } : l)),
  };
  return validated(candidate);
}

/**
 * Atomically move multiple tiles to new grid positions.
 *
 * ATOMIC multi-move: applies ALL position updates to a candidate board,
 * validates ONCE through BoardSchema + bounds + referential integrity.
 * All-or-nothing: if ANY move fails validation, the entire op fails with
 * the first detected error. NEVER mutates its input.
 *
 * Use this when the preview (computeDisplacements) has determined a set of
 * positions — dragged tile + displaced neighbors — and the commit must
 * match EXACTLY the preview showed.
 *
 * Errors:
 *   - 'unknown-tile' — any tileId in moves is not on the board.
 *   - 'invalid-board' — the resulting board fails BoardSchema.
 */
export function moveTiles(
  board: Board,
  moves: ReadonlyArray<{ readonly tileId: string; readonly position: Position }>,
): BoardOpResult {
  // Validate all tile ids exist before touching layout.
  for (const move of moves) {
    if (!hasTile(board, move.tileId)) {
      return err('unknown-tile', `no tile "${move.tileId}" on the board`);
    }
  }

  // Build a lookup from tileId → new position (last entry wins for duplicates).
  const positionMap = new Map<string, Position>(
    moves.map(({ tileId, position }) => [tileId, position]),
  );

  // Apply all position updates immutably.
  const candidateLayout = board.layout.map((l) => {
    const newPos = positionMap.get(l.tileId);
    return newPos !== undefined ? { ...l, x: newPos.x, y: newPos.y } : l;
  });

  const candidate = { ...board, layout: candidateLayout };
  return validated(candidate);
}
